import { Events, FilterChangedEvent } from '@ag-grid-community/core'
import { Injectable, OnDestroy } from '@angular/core'
import { restoreGridState } from '@helios/generic-components'
import {
  RootState,
  SavedView,
  selectActiveSavedViewForGrid,
  selectActiveWorkspace,
  selectSavedViewExistsForGridKey,
  updateSavedView,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { AgGridApi } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import * as lodash from 'lodash'
import { Subject } from 'rxjs'
import { delay, map, take, takeUntil, tap } from 'rxjs/operators'
import { WorkspaceService } from './workspace.service'

export interface WorkspaceUpdateEventObject {
  api?: AgGridApi
  panelSettings?: { [key: string]: unknown }
}
@Injectable({
  providedIn: 'root',
})
export class WorkspaceGridSavedViewService implements OnDestroy {
  private activeWorkspacePrefix?: string

  private readonly onDestroy$ = new Subject<void>()
  constructor(
    private readonly store: Store<RootState>,
    private readonly workspaceService: WorkspaceService,
  ) {
    store
      .select(selectActiveWorkspace)
      .pipe(
        takeUntil(this.onDestroy$),
        tap(
          workspace =>
            (this.activeWorkspacePrefix = workspace?.workspacePrefix),
        ),
      )
      .subscribe()
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  /**
   * Loads the saved view for the grid (or creates a new one)
   * Then watches the grid api for updates and saves any updates to the saved view
   * ASYNC! - you should await this before setting any filters to avoid a race condition
   * isPersistent - flag to persist savedView in ui storage (amazon s3), by default it is enabled
   *
   * @param gridApi
   * @param gridKey
   * @param deregisterSubject$
   * @param isPersistent
   * @param panelSettings
   * @param beforeRestore - called with the view just before its grid state is restored, for state
   * the ColDefs depend on (e.g. a column's chosen type) that has to be in place before the column
   * and filter state are applied onto them
   */
  async manageSavedView(
    gridApi: AgGridApi,
    gridKey: string,
    deregisterSubject$: Subject<void>,
    isPersistent: boolean = true,
    panelSettings?: { [key: string]: unknown },
    beforeRestore?: (savedView: AgGridState) => void,
  ): Promise<AgGridState> {
    await this.createSavedViewIfDoesNotExist(
      gridKey,
      gridApi,
      deregisterSubject$,
      isPersistent,
      panelSettings,
    )

    return this.store
      .pipe(
        takeUntil(deregisterSubject$),
        selectActiveSavedViewForGrid(gridKey),
        take(1),
        tap(savedView => {
          beforeRestore?.(savedView.savedView)
          restoreGridState(savedView.savedView, gridApi)
        }),
        delay(100),
        tap(savedView => {
          this.registerEvents(savedView, gridApi, deregisterSubject$, gridKey)
        }),
        map(v => v.savedView),
      )
      .toPromise()
  }

  async getSavedPanelSettings(gridKey: string) {
    return await this.store
      .pipe(
        selectActiveSavedViewForGrid(gridKey),
        take(1),
        map(v => v.savedView.panelSettings),
      )
      .toPromise()
  }

  async updatePanelSettingsOnly(
    gridKey: string,
    panelSetting: { key: string; value: unknown },
  ) {
    const existingView = await this.store
      .pipe(
        selectActiveSavedViewForGrid(gridKey),
        take(1),
        map(v => v),
      )
      .toPromise()
    const existingPanelSettings = existingView.savedView.panelSettings as {
      [key: string]: unknown
    }
    if (
      panelSetting.key in existingPanelSettings &&
      existingPanelSettings[panelSetting.key] == panelSetting.value
    ) {
      return
    }
    const viewPanelSettings = {
      ...existingPanelSettings,
      [panelSetting.key]: panelSetting.value,
    }

    const updatedSavedView: SavedView = {
      ...existingView,
      savedView: {
        ...existingView.savedView,
        panelSettings: viewPanelSettings,
      },
    }

    this.store.dispatch(updateSavedView(updatedSavedView))
  }

  private registerEvents(
    savedView: SavedView,
    gridApi: AgGridApi,
    destroySubject$: Subject<void>,
    gridKey: string,
  ) {
    //https://www.ag-grid.com/javascript-data-grid/grid-events/
    const events = [
      Events.EVENT_DISPLAYED_COLUMNS_CHANGED,
      Events.EVENT_COLUMN_MOVED,
      Events.EVENT_COLUMN_RESIZED,
      Events.EVENT_COLUMN_PINNED,
      Events.EVENT_SORT_CHANGED,
      'panelSettingsChanged',
    ]

    const bulkModalKeys = [
      'Bulk-wire-workspace-key',
      'Bulk-security-transfer-out-workspace-key',
      'Bulk-load-workspace-key',
      'Bulk-FX-Spot-workspace-key',
      'Bulk-currency-forward-workspace-key',
      'Bulk-direct-transfer-workspace-key',
      'Bulk-cash-entry-workspace-key',
      'Bulk-trade-entry-workspace-key',
      'Bulk-security-transfer-in-workspace-key-mode-create',
      'Bulk-security-transfer-in-workspace-key-mode-external',
      'Bulk-security-transfer-in-workspace-key-mode-edit',
    ]

    // Registering Event filter change only for external file viewer panel
    // Don't register EVENT_FILTER_CHANGED event for other panels specially position, transactions panel
    // since other panel calls api.onFilterChange() which somehow resets column layout
    // Moreover, grid key is hardcoded to prevent circular dependency
    if (
      gridKey.includes('external-file-viewer-ggo2q') ||
      this.configuredPanelHasSettings(gridKey)
    ) {
      events.push(Events.EVENT_FILTER_CHANGED)
    }

    if (bulkModalKeys.includes(gridKey))
      events.splice(events.indexOf(Events.EVENT_SORT_CHANGED), 1)

    events.forEach(eventType =>
      this.registerUpdateEvent(eventType, savedView, gridApi, destroySubject$),
    )
  }

  private registerUpdateEvent(
    eventType: string,
    savedView: SavedView,
    gridApi: AgGridApi,
    destroySubject$: Subject<void>,
  ) {
    const gridListener = async (event: AgGridApi) => {
      let currentPanelSettings: unknown
      if (this.configuredPanelHasSettings(savedView.gridKey)) {
        currentPanelSettings = await this.store
          .pipe(
            selectActiveSavedViewForGrid(savedView.gridKey),
            take(1),
            map(v => v.savedView.panelSettings ?? {}),
          )
          .toPromise()

        /* return/skip if filter change event and
              a) Panel settings empty never set for panel that is the panel is fresh after our change for PVAL-20407 OR
              b) If panel settings are set but clearFiltersOnAccountLoad is true OR
              c) the filter changed is called due to data change should not be considered for workspace save
          */
        if (
          eventType == Events.EVENT_FILTER_CHANGED &&
          (!Object.keys(currentPanelSettings as { [key: string]: unknown })
            .length ||
            (currentPanelSettings as { [key: string]: unknown })[
              'clearFiltersOnAccountLoad'
            ] ||
            (event as FilterChangedEvent).afterDataChange)
        ) {
          return
        }
      }

      const paramsForView = {
        api: event,
        panelSettings: currentPanelSettings ?? {},
      } as WorkspaceUpdateEventObject
      const updatedSavedView: SavedView = {
        ...savedView,
        savedView: this.getSavedViewGridState(paramsForView),
      }
      this.store.dispatch(updateSavedView(updatedSavedView))
    }
    const panelSettingsListener = (event: Event) => {
      const paramsForView = (event as CustomEvent)
        .detail as WorkspaceUpdateEventObject
      const updatedSavedView: SavedView = {
        ...savedView,
        savedView: this.getSavedViewGridState(paramsForView),
      }
      this.store.dispatch(updateSavedView(updatedSavedView))
    }
    if (eventType === Events.EVENT_COLUMN_RESIZED) {
      // to prevent resize column listeners being called multiple times
      gridApi.api.addEventListener(
        eventType,
        lodash.debounce(gridListener, 300),
      )
    } else {
      gridApi.api.addEventListener(
        eventType,
        eventType === 'panelSettingsChanged'
          ? panelSettingsListener
          : gridListener,
      )
    }

    destroySubject$
      .pipe(
        tap(() => {
          gridApi.api.removeEventListener(
            eventType,
            eventType === 'panelSettingsChanged'
              ? panelSettingsListener
              : gridListener,
          )
        }),
      )
      .subscribe()
  }

  private async createSavedViewIfDoesNotExist(
    gridKey: string,
    gridApi: AgGridApi,
    destroySubject$: Subject<void>,
    isPersistent: boolean,
    panelSettings?: { [key: string]: unknown },
  ) {
    const savedViewExists = await this.store
      .pipe(
        selectSavedViewExistsForGridKey(gridKey),
        takeUntil(destroySubject$),
        take(1),
      )
      .toPromise()
    if (savedViewExists) return
    const newSavedView = await this.createSavedView(
      gridKey,
      gridApi,
      isPersistent,
      panelSettings,
    )
    if (newSavedView == null) return
    this.store.dispatch(updateSavedView(newSavedView))
  }

  private async createSavedView(
    gridKey: string,
    gridApi: AgGridApi,
    isPersistent: boolean,
    panelSettings?: { [key: string]: unknown },
  ): Promise<SavedView | void> {
    if (this.activeWorkspacePrefix == null) return
    const gridState = this.getSavedViewGridState({
      api: gridApi,
      panelSettings,
    })

    const savedViewPrefix = await this.workspaceService.createSavedView(
      this.activeWorkspacePrefix,
      gridKey,
      gridState,
      isPersistent,
    )
    if (savedViewPrefix != null)
      return {
        savedViewPrefix,
        gridKey,
        savedView: gridState,
      } as SavedView
  }

  private getSavedViewGridState(
    workspaceUpdateEventObject?: WorkspaceUpdateEventObject,
  ) {
    const api = workspaceUpdateEventObject?.api
    if (api == null) return {}

    const columnState = api.columnApi.getColumnState()
    const columnGroupState = api.columnApi.getColumnGroupState()

    const filterState = api.api.getFilterModel()

    const panelSettings: { [key: string]: unknown } =
      workspaceUpdateEventObject?.panelSettings ?? {}
    const response = {
      columnState,
      columnGroupState,
      filterState,
      panelSettings,
    }
    if (
      !Object.keys(response.panelSettings as { [key: string]: unknown })
        .length ||
      response.panelSettings['clearFiltersOnAccountLoad']
    ) {
      response.filterState = {}
    }
    return response
  }

  configuredPanelHasSettings(gridKey: string) {
    return (
      gridKey.includes('clearwater-transactions-eny6w') ||
      gridKey.includes('breaks-e0xgt') ||
      gridKey.includes('distributions-4s5m0') ||
      gridKey.includes('account-list-3g7g0') ||
      gridKey.includes('external-file-manager-qas1y') ||
      gridKey.includes('jobs-69lgk') ||
      gridKey.includes('positions-3wfyp') ||
      gridKey.includes('positions-2-p7m2q') ||
      gridKey.includes('external-transactions-eny6w') ||
      gridKey.includes('approvals-nnqck') ||
      gridKey.includes('external-accounts-1j0lt') ||
      gridKey.includes('smart-suspense-ufahp-suspense-gl-table') ||
      gridKey.includes('smart-suspense-ufahp-aging-opr-table')
    )
  }
}
